export interface IPpuStatusConfig {
  label: string
  color: string
}

/*
 * Стадии = ImprovementSuggestion.WorkflowStatus на бэке.
 * implemented / on_revision — старые ключи, бэк их больше не отдаёт.
 */
export const PPU_STATUS_MAP: Record<string, IPpuStatusConfig> = {
  pending_department_head: {
    label: 'На согласовании у руководителя',
    color: 'info',
  },
  returned_to_author: {
    label: 'Требует корректировки автора',
    color: 'primary',
  },
  on_revision: {
    label: 'Требует корректировки автора',
    color: 'primary',
  },
  pending_commission_chair: {
    label: 'На рассмотрении у председателя комиссии',
    color: 'warning',
  },
  in_agreement: {
    label: 'На рассмотрении у комиссии',
    color: 'warning',
  },
  agreement_complete: {
    label: 'На решении у председателя комиссии',
    color: 'warning',
  },
  accepted: {
    label: 'Ожидает реализации',
    color: 'success',
  },
  implemented: {
    label: 'Ожидает реализации',
    color: 'success',
  },
  rejected: {
    label: 'Не рекомендовано к реализации',
    color: 'error',
  },
  archived: {
    label: 'Работа с предложением приостановлена',
    color: 'secondary',
  },
}

/* Агрегированный статус = ImprovementSuggestion.Status */
export const PPU_AGGREGATED_STATUS_MAP: Record<string, IPpuStatusConfig> = {
  new: {
    label: 'Новое',
    color: 'info',
  },
  in_progress: {
    label: 'В работе',
    color: 'warning',
  },
  under_revision: {
    label: 'На доработке',
    color: 'primary',
  },
  accepted: {
    label: 'Принято',
    color: 'success',
  },
  implemented: {
    label: 'Принято',
    color: 'success',
  },
  rejected: {
    label: 'Отклонено',
    color: 'error',
  },
  archived: {
    label: 'Отложено',
    color: 'secondary',
  },
}

export const PPU_AGGREGATED_STATUS_KEYS = [
  'new',
  'in_progress',
  'under_revision',
  'accepted',
  'rejected',
  'archived',
] as const

export function normalizeAggregatedStatusKey(status: string): string {
  if (status === 'implemented') return 'accepted'
  return status
}

export function normalizeStageKey(status: string): string {
  if (status === 'on_revision') return 'returned_to_author'
  if (status === 'implemented') return 'accepted'
  return status
}

export function getPpuStatusLabel(status?: string | null, displayFromBackend?: string | null): string {
  if (displayFromBackend && displayFromBackend.trim()) {
    return displayFromBackend
  }
  if (!status) return '----'
  return PPU_STATUS_MAP[status]?.label || status
}

export function getPpuAggregatedStatusLabel(status?: string | null, displayFromBackend?: string | null): string {
  if (displayFromBackend && displayFromBackend.trim()) {
    return displayFromBackend
  }
  if (!status) return '----'
  return PPU_AGGREGATED_STATUS_MAP[status]?.label || status
}

export function getPpuStatusColor(status?: string | null): string {
  if (!status) return 'info'
  return PPU_STATUS_MAP[status]?.color || 'info'
}

export function getPpuAggregatedStatusColor(status?: string | null): string {
  if (!status) return 'info'
  return PPU_AGGREGATED_STATUS_MAP[status]?.color || 'info'
}

const PPU_STAGE_ONLY_KEYS = new Set([
  'pending_department_head',
  'returned_to_author',
  'on_revision',
  'pending_commission_chair',
  'in_agreement',
  'agreement_complete',
])

/** Ключи, которых нет у заявок/НС — по ним узнаём виджет ППУ без target */
export function isPpuSpecificStatusKey(status: string): boolean {
  const stageKey = normalizeStageKey(status)
  const aggKey = normalizeAggregatedStatusKey(status)
  return PPU_STAGE_ONLY_KEYS.has(stageKey) || aggKey === 'under_revision'
}

/*
 * Подпись и цвет для графиков: стадии — из PPU_STATUS_MAP,
 * пересечение с агрегатами (accepted/rejected/archived) — как статус.
 */
export function getPpuMapperConfig(status?: string | null): IPpuStatusConfig | undefined {
  if (!status) return undefined
  const stageKey = normalizeStageKey(status)
  const aggKey = normalizeAggregatedStatusKey(status)
  if (PPU_STAGE_ONLY_KEYS.has(stageKey))
    return PPU_STATUS_MAP[stageKey]
  return PPU_AGGREGATED_STATUS_MAP[aggKey] || PPU_STATUS_MAP[stageKey]
}
